/**
 * cart-custom.js
 * Farmacias Arrocha — Módulo 2: Sincronización de precios en carrito
 *
 * Propósito:
 *   Garantizar que los precios mostrados en la página del carrito (/cart)
 *   siempre reflejen el estado actual de los productos en Shopify, incluyendo
 *   los descuentos y promociones configurados en la aplicación Selly.
 *
 * Problema que resuelve:
 *   Cuando un cliente tiene un carrito abandonado y regresa después de que
 *   Arrocha modificó precios o activó/desactivó una promoción en Selly,
 *   el carrito puede mostrar precios desactualizados. Este script fuerza
 *   la sincronización de precios y una recarga completa de la página para
 *   que Selly re-evalúe sus Discount Functions y muestre los precios correctos.
 *
 * Estrategia de detección de regreso al carrito:
 *   - DOMContentLoaded: primera carga de la página
 *   - window blur/focus: el usuario cambia de ventana o pestaña
 *   - document mouseleave/mouseenter: el cursor sale/entra de la ventana
 *     (necesario para escenarios de doble monitor)
 *
 * Carga condicional:
 *   Este archivo se carga únicamente en la página del carrito (/cart) via
 *   condicional Liquid en theme.liquid:
 *     {% if template.name == 'cart' %}
 *       <script src="{{ 'cart-custom.js' | asset_url }}" defer></script>
 *     {% endif %}
 *   Esto evita descargar el archivo en otras páginas y mejora la performance.
 *
 * Encapsulación:
 *   Todo el código está envuelto en una IIFE (Immediately Invoked Function
 *   Expression) para evitar contaminar el scope global de window y proteger
 *   contra colisiones con otras apps de Shopify.
 *
 * Modo debug:
 *   El archivo incluye una constante DEBUG que controla los console.log.
 *   En producción debe estar en false. Para fase/troubleshooting cambiar a true.
 *
 * Dependencias:
 *   - Shopify AJAX Cart API (/cart.js, /cart/update.js)
 *   - Selly - Promotion & Pricing (Treedify) en modo Discount Function
 *
 * Autor: Informatica EasyGo
 * Versión: 2.0.0
 * Fecha: Mayo 2026
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     CONFIGURACIÓN
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Flag de debug.
   * Cuando es true, imprime mensajes en la consola del navegador para facilitar
   * el seguimiento de eventos (DOM ready, blur, focus, mouseleave, mouseenter, click).
   * En producción debe estar en false para evitar ruido en la consola del cliente.
   * En fase/troubleshooting cambiar a true para ver el flujo de eventos.
   */
  var DEBUG = true;

  /**
   * Clave de sessionStorage usada como flag para evitar recargas infinitas.
   * Cuando la sincronización se ejecuta, esta clave se marca con '1'.
   * Los eventos blur y mouseleave la limpian para permitir la próxima sincronización.
   */
  var SYNC_FLAG_KEY = 'arrocha_cart_synced';

  /**
   * Path de la página del carrito.
   * Todos los listeners verifican este path antes de ejecutar lógica
   * para asegurar que solo actúen en /cart y no en otras páginas.
   */
  var CART_PATH = '/cart';

  /* ═══════════════════════════════════════════════════════════════════════
     UTILIDADES
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * log
   *
   * Wrapper de console.log que solo imprime si DEBUG === true.
   * Permite mantener los mensajes de debug en el código sin generar
   * ruido en la consola del navegador en producción.
   *
   * @param {...any} args - Argumentos a imprimir (mismo formato que console.log)
   */
  function log() {
    if (DEBUG) {
      console.log.apply(console, arguments);
    }
  }

  /**
   * isCartPage
   *
   * Verifica si el usuario está actualmente en la página del carrito.
   * Se usa como guardia al inicio de cada listener para evitar ejecutar
   * lógica en páginas distintas a /cart.
   *
   * @returns {boolean} true si la URL actual es /cart
   */
  function isCartPage() {
    return window.location.pathname === CART_PATH;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SINCRONIZACIÓN DE CARRITO
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * refreshCartPrices
   *
   * Obtiene el estado actual del carrito via /cart.js y llama a /cart/update.js
   * con las mismas cantidades. Esto fuerza a Shopify a recalcular los precios
   * base de los productos según el admin (precio actualizado en backoffice).
   *
   * Nota: Este endpoint NO re-evalúa las Discount Functions de Selly.
   * Para que Selly aplique sus descuentos correctamente, es necesario
   * hacer una recarga completa de la página (window.location.reload).
   */
  var refreshCartPrices = async function () {
    var response = await fetch('/cart.js');
    var cart = await response.json();

    if (!cart.items || cart.items.length === 0) return;

    var updates = {};
    cart.items.forEach(function (item) {
      updates[item.key] = item.quantity;
    });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  };

  /**
   * syncCart
   *
   * Función principal de sincronización. Utiliza sessionStorage como flag
   * para evitar recargas infinitas. El flujo es:
   *   1. Verificar si ya se sincronizó en esta sesión (flag arrocha_cart_synced)
   *   2. Si no se ha sincronizado: marcar el flag, actualizar precios y recargar
   *   3. Si ya se sincronizó: no hacer nada (evitar loop)
   *
   * El flag se limpia via blur/mouseleave para que la próxima vez que el usuario
   * regrese al carrito, se vuelva a sincronizar.
   */
  var syncCart = async function () {
    if (sessionStorage.getItem(SYNC_FLAG_KEY)) return;
    sessionStorage.setItem(SYNC_FLAG_KEY, '1');
    log('[Arrocha Cart] Syncing cart...');
    await refreshCartPrices();
    window.location.reload();
  };

  /* ═══════════════════════════════════════════════════════════════════════
     EVENT LISTENERS
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * DOMContentLoaded
   *
   * Ejecuta la sincronización cuando la página del carrito termina de cargar.
   * Solo actúa si la URL actual es /cart.
   */
  document.addEventListener('DOMContentLoaded', async function () {
    if (!isCartPage()) return;
    log('[Arrocha Cart] DOM event');
    await syncCart();
  });

  /**
   * window blur
   *
   * Se dispara cuando la ventana del navegador pierde el foco
   * (el usuario cambia a otra ventana o aplicación).
   * Limpia el flag de sincronización para que al regresar
   * se vuelva a sincronizar el carrito.
   */
  window.addEventListener('blur', function () {
    if (!isCartPage()) return;
    log('[Arrocha Cart] Window blur — flag cleared');
    sessionStorage.removeItem(SYNC_FLAG_KEY);
  });

  /**
   * window focus
   *
   * Se dispara cuando la ventana del navegador recupera el foco
   * (el usuario regresa desde otra ventana o pestaña).
   * Ejecuta la sincronización del carrito.
   */
  window.addEventListener('focus', function () {
    if (!isCartPage()) return;
    log('[Arrocha Cart] Window focus');
    syncCart();
  });

  /**
   * document mouseleave
   *
   * Se dispara cuando el cursor sale completamente de la ventana del navegador
   * (por cualquier borde: arriba, abajo, izquierda, derecha).
   * Necesario para escenarios de doble monitor donde el usuario mueve el cursor
   * a otra pantalla sin cambiar el foco de la ventana.
   * Limpia el flag para que al regresar el cursor se sincronice el carrito.
   */
  document.addEventListener('mouseleave', function (e) {
    if (!isCartPage()) return;
    if (e.clientY <= 0 || e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
      log('[Arrocha Cart] Mouse left window — flag cleared');
      sessionStorage.removeItem(SYNC_FLAG_KEY);
    }
  });

  /**
   * document mouseenter
   *
   * Se dispara cuando el cursor entra nuevamente a la ventana del navegador.
   * Complementa el evento mouseleave para detectar el regreso del usuario
   * en escenarios de doble monitor.
   * Ejecuta la sincronización del carrito.
   */
  document.addEventListener('mouseenter', function () {
    if (!isCartPage()) return;
    log('[Arrocha Cart] Mouse entered window');
    syncCart();
  });

  /**
   * click en botón de checkout
   *
   * Intercepta el clic en el botón "Check out" del carrito.
   * Antes de redirigir al checkout, actualiza los precios via /cart/update.js
   * para asegurar que Shopify tenga los precios más recientes del backoffice.
   * Luego redirige manualmente a /checkout.
   *
   * Nota: El descuento de Selly se aplica correctamente en el checkout
   * cuando el cliente llega desde el carrito, ya que Selly habrá evaluado
   * sus Discount Functions durante la sincronización previa del carrito.
   */
  document.addEventListener('click', async function (e) {
    var checkoutBtn = e.target.closest('[name="checkout"]');
    if (!checkoutBtn) return;
    e.preventDefault();
    log('[Arrocha Cart] Click event');
    await refreshCartPrices();
    window.location.href = '/checkout';
  });

  /* ═══════════════════════════════════════════════════════════════════════
     INICIALIZACIÓN
     ═══════════════════════════════════════════════════════════════════════ */

  log('[Arrocha Cart] Sincronización de carrito activa');

})(); 